import { useEffect, useState } from 'react';
import { getProjects } from '../api/client';
import { ProjectInfo } from '../api/types';
import { findExtra, ProjectExtra } from '../data/projectExtras';
import { useLanguage } from '../i18n/LanguageContext';
import ProjectCard, { displayDomain } from './ProjectCard';

interface Entry {
  project: ProjectInfo;
  extra?: ProjectExtra;
}

function SectionHead({ id, eyebrow, title, dim }: { id?: string; eyebrow: string; title: string; dim: string }) {
  return (
    <div id={id} className="section-head">
      <div className="eyebrow rv">{eyebrow}</div>
      <h2 className="section-title wide rv-clip">
        {title} <span className="dim">{dim}</span>
      </h2>
    </div>
  );
}

function Work() {
  const { t, lang } = useLanguage();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    getProjects()
      .then((projects) =>
        setEntries(
          [...projects]
            .sort((a, b) => a.sort_order - b.sort_order)
            .map((project) => ({ project, extra: findExtra(project.link_url) }))
        )
      )
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const work = entries.filter((e) => !e.extra || e.extra.group === 'work');
  const clients = entries.filter((e) => e.extra?.group === 'client');
  const studies = entries.filter((e) => e.extra?.group === 'study');
  const num = (i: number) => String(i + 1).padStart(2, '0');

  return (
    <>
      <section id="work" className="work">
        <SectionHead eyebrow={t.work.eyebrow} title={t.work.heading} dim={t.work.heading2} />
        {loading && <p className="status">{t.work.loading}</p>}
        {error && <p className="status">{t.work.error}</p>}
        <div className="work-list">
          {work.map((e, i) => (
            <ProjectCard key={e.project.id} project={e.project} extra={e.extra} num={num(i)} />
          ))}
          {clients.length > 0 && (
            <SectionHead id="clients" eyebrow={t.work.clientEyebrow} title={t.work.clientHeading} dim={t.work.clientHeading2} />
          )}
          {clients.map((e, i) => (
            <ProjectCard key={e.project.id} project={e.project} extra={e.extra} num={num(work.length + i)} />
          ))}
        </div>
      </section>

      {studies.length > 0 && (
        <section className="studies">
          <SectionHead eyebrow={t.studies.eyebrow} title={t.studies.heading} dim={t.studies.heading2} />
          <div className="studies-grid">
            {studies.map(({ project, extra }) => (
              <a key={project.id} href={project.link_url} target="_blank" rel="noreferrer" className="study-card rv-scale">
                <div className="study-media">
                  {project.image_urls[0] && (
                    <img className="rv-img" src={project.image_urls[0]} alt={extra!.title} loading="lazy" />
                  )}
                </div>
                <div className="study-body">
                  <div className="study-text">
                    <span className="study-title">{extra!.title}</span>
                    <span className="study-desc">{extra!.desc[lang]}</span>
                  </div>
                  <span className="study-stack" title={displayDomain(project.link_url)}>
                    {extra!.stack} ↗
                  </span>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

export default Work;
